import React, { useState, useEffect, useRef } from 'react';
// Import required functions from Firebase SDK
import { initializeApp } from "firebase/app";
import { 
    getAuth, 
    signInWithEmailAndPassword, 
    onAuthStateChanged, 
    signOut 
} from "firebase/auth";
import { 
    getFirestore, 
    collection, 
    addDoc, 
    serverTimestamp, 
    onSnapshot,
    query,
    doc,
    updateDoc,
    arrayUnion,
    orderBy,
    limit,
    getDocs,
    startAfter
} from "firebase/firestore";

// Firebase Configuration
const firebaseConfig = {
  apiKey: "AIzaSyCLPIiHmdFQqFUmzptTIMSEJjwVGtj9azw",
  authDomain: "reportdomain-6c41e.firebaseapp.com",
  projectId: "reportdomain-6c41e",
  storageBucket: "reportdomain-6c41e.firebasestorage.app",
  messagingSenderId: "961618036099",
  appId: "1:961618036099:web:d33e214fe8b313f766a2b6"
};
  
// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

//================================================================================
// Login Page Component
//================================================================================
function LoginPage({ onLogin, error, setLoading }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        await onLogin(email, password);
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-slate-800 rounded-2xl shadow-xl p-8 space-y-6 border border-slate-700">
                <div className="text-center">
                    <h1 className="text-3xl font-medium text-slate-100 flex items-center justify-center gap-3">
                        <i className="fas fa-tools text-blue-400"></i>
                        <span>Report Tools V1.7</span>
                    </h1>
                    <p className="text-slate-400 mt-2 font-normal">Please sign in to continue</p>
                </div>
                {error && <p className="bg-red-500/20 text-red-400 p-3 rounded-lg text-center font-normal">{error}</p>}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-normal text-slate-300 mb-1">Email Address</label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 transition font-normal"
                            placeholder="user@example.com"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-normal text-slate-300 mb-1">Password</label>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 transition font-normal"
                            placeholder="••••••••"
                        />
                    </div>
                    <div className="pt-2">
                        <button type="submit" className="w-full bg-blue-600 text-white p-3 rounded-lg font-medium hover:bg-blue-700 transition flex items-center justify-center gap-2">
                            <i className="fas fa-sign-in-alt"></i>
                            <span>Sign In</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

//================================================================================
// Header & Navigation Component
//================================================================================
function Header({ user, onNavigate, onLogout, currentPage }) {
    const navItems = [
        { id: 'dashboard', icon: 'fa-home', label: 'Dashboard' },
        { id: 'reports', icon: 'fa-file-alt', label: 'Reports' },
        { id: 'pbn_report', icon: 'fa-network-wired', label: 'PBN' },
        { id: 'tasks', icon: 'fa-tasks', label: 'Tasks' },
    ];

    return (
        <header className="bg-slate-900/80 backdrop-blur-sm p-4 sm:p-6 flex-shrink-0 border-b border-slate-800 sticky top-0 z-10">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
                <div className="flex items-center gap-6">
                     <h1 className="text-xl font-medium text-slate-100 hidden md:flex items-center gap-2">
                        <i className="fas fa-tools text-blue-400"></i>
                        <span>Report Tools</span>
                    </h1>
                     <nav className="flex items-center gap-2 bg-slate-800 p-1.5 rounded-lg overflow-x-auto scrollbar-hide">
                        {navItems.map(item => (
                            <button 
                                key={item.id}
                                onClick={() => onNavigate(item.id)}
                                className={`px-3 py-1.5 text-sm font-normal rounded-md transition flex items-center gap-2 whitespace-nowrap ${currentPage === item.id ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-300 hover:bg-slate-700'}`}
                            >
                                <i className={`fas ${item.icon}`}></i>
                                <span className="hidden sm:inline">{item.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>
                <div className="flex items-center gap-4">
                    <button onClick={onLogout} className="bg-red-500/10 text-red-500 border border-red-500/20 px-3 py-2 rounded-lg font-normal hover:bg-red-500 hover:text-white transition flex items-center gap-2" title="Logout">
                        <i className="fas fa-sign-out-alt"></i>
                        <span className="hidden lg:inline text-xs">Logout</span>
                    </button>
                </div>
            </div>
        </header>
    );
}

//================================================================================
// Dashboard Page Component
//================================================================================
function DashboardPage({ user, onNavigate }) {
    const features = [
        { id: 'reports', title: 'Status Reports', description: 'Monitor, search, and manage all site rankings and status logs.', icon: 'fa-file-alt' },
        { id: 'pbn_report', title: 'PBN Management', description: 'Manage Private Blog Network domains and track their progress.', icon: 'fa-network-wired' },
        { id: 'tasks', title: 'Team Tasks', description: 'Assign, track, and update daily operational tasks for the team.', icon: 'fa-tasks' }
    ];

    return (
        <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto">
            <h1 className="text-3xl font-medium text-slate-100 mb-2 uppercase tracking-tight">Welcome back, {user.email.split('@')[0]}</h1>
            <p className="text-slate-400 mb-10 font-normal">Choose a module from the dashboard to start managing your workflow.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {features.map(feature => (
                    <div 
                        key={feature.id} 
                        onClick={() => onNavigate(feature.id)}
                        className="group bg-slate-800 p-8 rounded-2xl border border-slate-700 hover:border-blue-500 hover:bg-slate-700/30 transition-all cursor-pointer transform hover:-translate-y-2 shadow-xl"
                    >
                        <div className="flex items-center gap-5 mb-6">
                            <div className="bg-blue-600/10 w-14 h-14 rounded-2xl flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all text-blue-500">
                                <i className={`fas ${feature.icon} text-2xl`}></i>
                            </div>
                            <h2 className="text-2xl font-medium text-slate-100 group-hover:text-blue-400 transition-colors">{feature.title}</h2>
                        </div>
                        <p className="text-slate-400 leading-relaxed font-normal">{feature.description}</p>
                        <div className="mt-8 flex items-center text-blue-500 font-medium text-sm gap-2">
                            <span>Access Module</span>
                            <i className="fas fa-arrow-right group-hover:translate-x-2 transition-transform"></i>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

//================================================================================
// Reports List Page Component (with Auto Lazyload)
//================================================================================
function ReportsPage({ onNavigate }) {
    const [reports, setReports] = useState([]);
    const [lastVisible, setLastVisible] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [selectedDate, setSelectedDate] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const loaderRef = useRef(null);
    const REPORTS_PER_PAGE = 8;

    useEffect(() => {
        setLoading(true);
        const q = query(collection(db, "reports"), orderBy("createdAt", "desc"), limit(REPORTS_PER_PAGE));
        getDocs(q).then(documentSnapshots => {
            const reportsData = documentSnapshots.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setReports(reportsData);
            if (documentSnapshots.docs.length > 0) {
                setLastVisible(documentSnapshots.docs[documentSnapshots.docs.length - 1]);
            }
            setHasMore(documentSnapshots.docs.length === REPORTS_PER_PAGE);
            setLoading(false);
        }).catch(err => { console.error(err); setLoading(false); });
    }, []);

    const fetchMoreReports = () => {
        if (!hasMore || loadingMore || !lastVisible) return;
        setLoadingMore(true);
        const q = query(collection(db, "reports"), orderBy("createdAt", "desc"), startAfter(lastVisible), limit(REPORTS_PER_PAGE));
        getDocs(q).then(documentSnapshots => {
            const newReportsData = documentSnapshots.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setReports(prev => [...prev, ...newReportsData]);
            if (documentSnapshots.docs.length > 0) {
                setLastVisible(documentSnapshots.docs[documentSnapshots.docs.length - 1]);
            }
            setHasMore(documentSnapshots.docs.length === REPORTS_PER_PAGE);
            setLoadingMore(false);
        }).catch(err => { console.error(err); setLoadingMore(false); });
    };

    useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !loadingMore) fetchMoreReports();
        }, { threshold: 0.1 });
        if (loaderRef.current) observer.observe(loaderRef.current);
        return () => { if (loaderRef.current) observer.unobserve(loaderRef.current); };
    }, [hasMore, loadingMore, lastVisible]);

    const filteredReports = reports.filter(report => {
        if (selectedDate && (report.createdAt ? new Date(report.createdAt.toDate()).toISOString().split('T')[0] : '') !== selectedDate) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            const searchable = [
                report.kategori,
                report.google_ranks?.map(r => `${r.domainName} ${r.ampDomain}`).join(' '),
                report.notes,
                report.authorEmail
            ].join(' ').toLowerCase();
            if (!searchable.includes(q)) return false;
        }
        return true;
    });

    const StatusBadge = ({ label, value }) => {
        const isNawala = value === 'Nawala';
        return (
            <div className="flex justify-between items-center bg-slate-900/50 p-2 rounded-lg border border-slate-700/50">
                <span className="text-slate-500 text-[10px] uppercase font-medium tracking-widest">{label}</span>
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded uppercase ${isNawala ? 'text-red-400 bg-red-400/10 border border-red-400/20' : 'text-green-400 bg-green-400/10 border border-green-400/20'}`}>
                    {value}
                </span>
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full bg-slate-950/20 font-normal">
            <div className="p-4 sm:p-6 md:p-8 flex-shrink-0">
                 <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
                        <div>
                            <h2 className="text-4xl font-medium text-slate-100 tracking-tighter">Status Reports</h2>
                            <p className="text-slate-400 text-sm mt-1 font-normal">Reviewing current site rankings and visibility status.</p>
                        </div>
                        <button onClick={() => onNavigate('new_report')} className="bg-blue-600 text-white h-14 px-8 rounded-2xl font-medium hover:bg-blue-700 transition shadow-xl flex items-center gap-3 active:scale-95">
                            <i className="fas fa-plus"></i>
                            <span>New Report</span>
                        </button>
                    </div>
                     <div className="p-5 bg-slate-800 rounded-2xl border border-slate-700 flex flex-col sm:flex-row gap-4 items-center shadow-lg">
                        <div className="relative flex-grow w-full">
                            <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"></i>
                            <input type="text" placeholder="Search by domain, operator, category..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full p-3 pl-12 bg-slate-700 border border-slate-600 rounded-xl text-white focus:ring-2 focus:ring-blue-500 transition outline-none font-normal"/>
                        </div>
                        <div className="relative w-full sm:w-auto">
                            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-full p-3 bg-slate-700 border border-slate-600 rounded-xl text-white focus:ring-2 focus:ring-blue-500 transition outline-none font-normal" style={{ colorScheme: 'dark' }} />
                        </div>
                        <button onClick={() => { setSearchQuery(''); setSelectedDate(''); }} className="w-full sm:w-auto bg-slate-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-500 transition flex items-center justify-center gap-2"><i className="fas fa-sync-alt"></i><span>Reset</span></button>
                    </div>
                </div>
            </div>
            <div className="flex-grow overflow-y-auto px-4 sm:px-6 md:px-8 pb-10">
                <div className="max-w-7xl mx-auto space-y-8">
                    {loading ? (
                        <div className="text-center py-20">
                            <div className="inline-block w-8 h-8 border-4 border-dashed rounded-full animate-spin border-blue-600 mb-4"></div>
                            <p className="text-slate-400 font-medium uppercase tracking-widest text-xs">Synchronizing Reports...</p>
                        </div>
                    ) : filteredReports.length > 0 ? filteredReports.map(report => (
                        <div key={report.id} className="bg-slate-800 p-8 rounded-3xl shadow-2xl border border-slate-700 transition-all hover:border-blue-500/50">
                             <div className="flex flex-col sm:flex-row justify-between sm:items-start mb-8 border-b border-slate-700 pb-6 gap-4">
                                <div>
                                    <h2 className="text-3xl font-medium text-blue-400 tracking-tight">{report.kategori}</h2>
                                    <div className="flex items-center gap-2 mt-2 text-slate-500 text-xs font-normal uppercase">
                                        <i className="fas fa-user-circle"></i>
                                        <span>{report.authorEmail}</span>
                                    </div>
                                </div>
                                <div className="text-xs font-normal text-slate-400 text-left sm:text-right bg-slate-900/50 p-3 rounded-xl border border-slate-700">
                                    <p className="text-blue-500 mb-1 tracking-widest font-medium">TIMESTAMP</p>
                                    <p>{report.createdAt ? new Date(report.createdAt.toDate()).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : 'Recently'}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                {report.google_ranks?.map((rank, idx) => (
                                    <div key={idx} className="bg-slate-900/40 p-6 rounded-2xl border border-slate-700 hover:bg-slate-900/60 transition-colors">
                                        <div className="flex justify-between items-center mb-5">
                                            <span className="bg-blue-600 text-white text-[10px] px-3 py-1 rounded-full font-medium uppercase tracking-widest">Rank {rank.rank}</span>
                                            <span className="text-blue-400 font-medium text-xs px-2 py-0.5 border border-blue-400/20 rounded bg-blue-400/5 uppercase">{rank.type}</span>
                                        </div>
                                        
                                        <div className="space-y-4 font-normal">
                                            <div>
                                                <p className="text-slate-500 text-[9px] uppercase font-medium tracking-widest mb-1">Landing Page Domain</p>
                                                <p className="text-slate-100 font-normal break-all text-sm leading-tight mb-2">{rank.domainName || 'N/A'}</p>
                                                <StatusBadge label="Domain Status" value={rank.domainStatus} />
                                            </div>
                                            <div className="pt-4 border-t border-slate-700/50">
                                                <p className="text-slate-500 text-[9px] uppercase font-medium tracking-widest mb-1">AMP Domain</p>
                                                <p className="text-slate-100 font-normal break-all text-sm leading-tight mb-2">{rank.ampDomain || 'N/A'}</p>
                                                <StatusBadge label="AMP Status" value={rank.ampStatus} />
                                            </div>
                                            <div className="pt-4 border-t border-slate-700/50">
                                                <p className="text-slate-500 text-[9px] uppercase font-medium tracking-widest mb-1">Cache Link</p>
                                                <p className="text-slate-100 font-normal break-all text-[11px] leading-tight mb-2 truncate" title={rank.linkCache}>{rank.linkCache || 'N/A'}</p>
                                                <StatusBadge label="Cache Status" value={rank.linkCacheStatus} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {(report.linkAds_domainAds || report.notes) && (
                                <div className="mt-8 pt-8 border-t border-slate-700 flex flex-col gap-6">
                                    {report.linkAds_domainAds && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="bg-slate-900/50 p-4 rounded-2xl border border-slate-700">
                                                <p className="text-slate-500 text-[10px] uppercase font-medium tracking-widest mb-1">Ads Domain</p>
                                                <p className="text-blue-300 font-normal">{report.linkAds_domainAds}</p>
                                            </div>
                                            <div className="bg-slate-900/50 p-4 rounded-2xl border border-slate-700">
                                                <p className="text-slate-500 text-[10px] uppercase font-medium tracking-widest mb-1">Shortlink</p>
                                                <p className="text-blue-300 font-normal">{report.linkAds_shortlink}</p>
                                            </div>
                                        </div>
                                    )}
                                    {report.notes && (
                                        <div className="bg-slate-900/20 p-5 rounded-2xl border border-slate-700/50">
                                            <h3 className="font-medium text-xs text-slate-500 uppercase tracking-widest mb-3">Operator Notes</h3>
                                            <p className="text-slate-300 text-sm whitespace-pre-wrap italic font-normal">"{report.notes}"</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )) : (<div className="text-center py-20 bg-slate-800 rounded-3xl shadow-lg border border-slate-700"><i className="far fa-folder-open fa-4x text-slate-600 mx-auto mb-4"></i><p className="text-slate-400 font-medium uppercase tracking-widest">No reports available in history.</p></div>)}
                    <div ref={loaderRef} className="h-20 flex justify-center items-center">
                        {loadingMore && <div className="text-slate-500 font-medium text-xs uppercase animate-pulse">Loading more logs...</div>}
                    </div>
                </div>
            </div>
        </div>
    );
}

//================================================================================
// New Report Form Component
//================================================================================
function NewReportForm({ user, onNavigate, setLoading }) {
    const KATEGORI_OPTIONS = ["TOTO855", "FANTASTIC4D", "BOS5000", "IRON4D", "CANDUAN188", "ETHER777", "ELLOSLOT", "PIKASLOT", "SPACEX168", "LINK5000", "CCTVSLOT", "CUAN855"];
    
    const [googleRanks, setGoogleRanks] = useState([{ 
        rank: '1', 
        type: 'MS', 
        domainName: '', 
        domainStatus: 'Active',
        ampDomain: '',
        ampStatus: 'Active',
        linkCache: '',
        linkCacheStatus: 'Active'
    }]);

    const [formData, setFormData] = useState({
        kategori: KATEGORI_OPTIONS[0],
        linkAds_domainAds: '', 
        linkAds_shortlink: '',
        notes: ''
    });

    const [error, setError] = useState('');

    const handleInputChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleRankChange = (index, field, value) => {
        const updated = [...googleRanks];
        updated[index][field] = value;
        setGoogleRanks(updated);
    };

    const addRankField = () => {
        setGoogleRanks([...googleRanks, { 
            rank: (googleRanks.length + 1).toString(), 
            type: 'MS', 
            domainName: '', 
            domainStatus: 'Active',
            ampDomain: '',
            ampStatus: 'Active',
            linkCache: '',
            linkCacheStatus: 'Active'
        }]);
    };

    const removeRankField = (index) => {
        if (googleRanks.length <= 1) return;
        const updated = googleRanks.filter((_, i) => i !== index);
        const renumbered = updated.map((r, i) => ({ ...r, rank: (i + 1).toString() }));
        setGoogleRanks(renumbered);
    };

    const sendTelegramNotification = async (finalData) => {
        const botToken = "8200057133:AAGiuk4quww5npY29mi5zB2XKwga39FPiOY";
        const chatTargets = [{ chatId: "-1002468968890" }, { chatId: "-1002410368560", threadId: 40045 }];
        const nawalaMarker = (value) => (value === "Nawala" ? "❗️" : "✅");
        const timestamp = new Date().toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' });

        let message = `*New Status Report: ${finalData.kategori}*\n*Time:* \`${timestamp}\`\n\n`;
        message += `Operator: \`${user.email}\`\n`;
        message += `--------------------------------------\n\n`;
        
        finalData.google_ranks.forEach(r => {
            message += `*RANK ${r.rank} (${r.type})*\n`;
            message += `  - Domain: \`${r.domainName || "-"}\` ${nawalaMarker(r.domainStatus)}\n`;
            message += `  - AMP: \`${r.ampDomain || "-"}\` ${nawalaMarker(r.ampStatus)}\n`;
            message += `  - Cache: \`${r.linkCache || "-"}\` ${nawalaMarker(r.linkCacheStatus)}\n\n`;
        });

        if (finalData.linkAds_domainAds) {
            message += `*Link ads*\n  - Domain: \`${finalData.linkAds_domainAds}\`\n  - Short: \`${finalData.linkAds_shortlink}\`\n\n`;
        }

        if (finalData.notes) message += `*Notes:*\n\`\`\`\n${finalData.notes}\n\`\`\`\n`;

        for (const target of chatTargets) {
            const payload = { chat_id: target.chatId, text: message, parse_mode: 'Markdown', ...(target.threadId && { message_thread_id: target.threadId }) };
            try {
                await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            } catch (err) { console.error(err); }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!googleRanks[0].domainName.trim()) { setError('Rank 1 domain cannot be empty.'); return; }
        setLoading(true); setError('');
        const finalReport = { ...formData, google_ranks: googleRanks, authorEmail: user.email, authorId: user.uid, createdAt: serverTimestamp() };
        try {
            await addDoc(collection(db, "reports"), finalReport);
            await sendTelegramNotification(finalReport);
            onNavigate('reports');
        } catch (err) { setError('Failed to submit report. Please check your connection.'); console.error(err); } 
        finally { setLoading(false); }
    };
    
    return (
        <div className="p-4 sm:p-6 md:p-8 h-full bg-slate-900 font-normal">
            <div className="max-w-4xl mx-auto bg-slate-800 rounded-3xl shadow-2xl border border-slate-700 h-full flex flex-col overflow-hidden">
                 <form onSubmit={handleSubmit} className="flex flex-col h-full">
                    <div className="p-10 pb-4 shrink-0 bg-slate-800/50 border-b border-slate-700/50">
                        <h1 className="text-4xl font-medium text-slate-100 tracking-tighter">Submit Report</h1>
                        <p className="text-slate-400 mt-1 font-normal">Detailed log of site rankings and active status.</p>
                        {error && <p className="bg-red-500/20 text-red-400 p-4 rounded-2xl text-center mt-6 border border-red-500/20 font-medium">{error}</p>}
                    </div>
                    
                    <div className="px-10 py-8 space-y-10 overflow-y-auto grow scrollbar-hide">
                        <div>
                            <label className="block text-xs font-medium text-slate-500 mb-3 uppercase tracking-widest">Select Category</label>
                            <select name="kategori" value={formData.kategori} onChange={handleInputChange} className="w-full p-4 bg-slate-700 border border-slate-600 rounded-2xl text-white font-medium focus:ring-2 focus:ring-blue-500 transition outline-none shadow-inner">
                                {KATEGORI_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        </div>
                        
                        <div className="space-y-8">
                            <h3 className="text-xs font-medium text-slate-500 uppercase tracking-[0.2em] border-b border-slate-700 pb-2">I. Google Rankings</h3>
                            {googleRanks.map((item, index) => (
                                <div key={index} className="p-8 bg-slate-900/60 rounded-3xl border border-slate-700 relative shadow-2xl">
                                    <div className="flex justify-between items-center mb-8">
                                        <div className="flex items-center gap-4">
                                            <span className="bg-blue-600 text-white text-xs px-4 py-1.5 rounded-full font-medium uppercase tracking-widest shadow-lg">Rank {item.rank}</span>
                                            <div className="flex bg-slate-700 p-1.5 rounded-xl border border-slate-600 shadow-inner">
                                                {['LP', 'MS'].map(t => (
                                                    <button key={t} type="button" onClick={() => handleRankChange(index, 'type', t)} className={`px-6 py-2 text-xs rounded-lg transition font-medium ${item.type === t ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}>{t}</button>
                                                ))}
                                            </div>
                                        </div>
                                        {index > 0 && (
                                            <button type="button" onClick={() => removeRankField(index)} className="text-slate-600 hover:text-red-500 transition p-3 hover:bg-red-500/10 rounded-2xl">
                                                <i className="fas fa-trash-alt text-lg"></i>
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 font-normal">
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-medium text-slate-500 uppercase tracking-widest">Domain MS/LP</label>
                                            <div className="flex gap-3">
                                                <input type="text" required placeholder="Domain Name" value={item.domainName} onChange={(e) => handleRankChange(index, 'domainName', e.target.value)} className="flex-grow p-3 bg-slate-800 border border-slate-600 rounded-xl text-white text-sm font-normal outline-none focus:border-blue-500 transition shadow-inner" />
                                                <select value={item.domainStatus} onChange={(e) => handleRankChange(index, 'domainStatus', e.target.value)} className="w-32 p-3 bg-slate-800 border border-slate-600 rounded-xl text-white text-sm font-medium outline-none focus:border-blue-500 transition shadow-inner">
                                                    <option value="Active">Active</option>
                                                    <option value="Nawala">Nawala</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <label className="text-[10px] font-medium text-slate-500 uppercase tracking-widest">AMP Domain</label>
                                            <div className="flex gap-3">
                                                <input type="text" placeholder="AMP Domain" value={item.ampDomain} onChange={(e) => handleRankChange(index, 'ampDomain', e.target.value)} className="flex-grow p-3 bg-slate-800 border border-slate-600 rounded-xl text-white text-sm font-normal outline-none focus:border-blue-500 transition shadow-inner" />
                                                <select value={item.ampStatus} onChange={(e) => handleRankChange(index, 'ampStatus', e.target.value)} className="w-32 p-3 bg-slate-800 border border-slate-600 rounded-xl text-white text-sm font-medium outline-none focus:border-blue-500 transition shadow-inner">
                                                    <option value="Active">Active</option>
                                                    <option value="Nawala">Nawala</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="space-y-3 md:col-span-2">
                                            <label className="text-[10px] font-medium text-slate-500 uppercase tracking-widest">Link Cache</label>
                                            <div className="flex gap-3">
                                                <input type="text" placeholder="Paste cache URL here" value={item.linkCache} onChange={(e) => handleRankChange(index, 'linkCache', e.target.value)} className="flex-grow p-3 bg-slate-800 border border-slate-600 rounded-xl text-white text-sm font-normal outline-none focus:border-blue-500 transition shadow-inner" />
                                                <select value={item.linkCacheStatus} onChange={(e) => handleRankChange(index, 'linkCacheStatus', e.target.value)} className="w-32 p-3 bg-slate-800 border border-slate-600 rounded-xl text-white text-sm font-medium outline-none focus:border-blue-500 transition shadow-inner">
                                                    <option value="Active">Active</option>
                                                    <option value="Nawala">Nawala</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button type="button" onClick={addRankField} className="w-full py-6 border-4 border-dashed border-slate-700 rounded-3xl text-slate-500 hover:text-blue-500 hover:border-blue-500/50 transition-all flex items-center justify-center gap-3 font-medium uppercase tracking-widest group shadow-lg">
                            <i className="fas fa-plus-circle text-xl group-hover:scale-125 transition-transform"></i> Add Another Rank
                        </button>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8 bg-slate-900/30 rounded-3xl border border-slate-700 shadow-2xl font-normal">
                             <div className="space-y-3">
                                <label className="text-[10px] font-medium text-slate-500 uppercase tracking-widest">Ads Domain</label>
                                <input type="text" name="linkAds_domainAds" value={formData.linkAds_domainAds} onChange={handleInputChange} className="w-full p-4 bg-slate-800 border border-slate-600 rounded-2xl text-white text-sm outline-none focus:border-blue-500 transition shadow-inner" placeholder="ads-domain.com" />
                             </div>
                             <div className="space-y-3">
                                <label className="text-[10px] font-medium text-slate-500 uppercase tracking-widest">Shortlink</label>
                                <input type="text" name="linkAds_shortlink" value={formData.linkAds_shortlink} onChange={handleInputChange} className="w-full p-4 bg-slate-800 border border-slate-600 rounded-2xl text-white text-sm outline-none focus:border-blue-500 transition shadow-inner" placeholder="bit.ly/xxxx" />
                             </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-slate-500 mb-3 uppercase tracking-widest">Additional Notes</label>
                            <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="4" className="w-full p-4 bg-slate-700 border border-slate-600 rounded-2xl text-white focus:ring-2 focus:ring-blue-500 transition outline-none shadow-inner font-normal" placeholder="Enter any extra details or instructions..."/>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 p-10 shrink-0 bg-slate-800/50 border-t border-slate-700/50">
                        <button type="submit" className="w-full bg-blue-600 text-white p-5 rounded-2xl font-medium text-lg hover:bg-blue-700 transition flex items-center justify-center gap-3 shadow-2xl active:scale-95 group">
                            <i className="fas fa-paper-plane group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform"></i>
                            <span>Submit Report</span>
                        </button>
                        <button type="button" onClick={() => onNavigate('reports')} className="w-full bg-slate-700 text-slate-200 p-5 rounded-2xl font-medium text-lg hover:bg-slate-600 transition flex items-center justify-center gap-3 active:scale-95">
                            <i className="fas fa-undo"></i>
                            <span>Back</span>
                        </button>
                    </div>
                 </form>
            </div>
        </div>
    );
}

//================================================================================
// PBN Page Component
//================================================================================
function PbnItem({ pbn, user }) {
    const [commentText, setCommentText] = useState('');
    const [loading, setLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!commentText.trim()) return;
        setLoading(true);
        try {
            await updateDoc(doc(db, 'pbns', pbn.id), { comments: arrayUnion({ text: commentText, authorEmail: user.email, createdAt: new Date() }) });
            setCommentText('');
        } catch (err) { console.error(err); }
        setLoading(false);
    };

    return (
        <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-xl font-normal">
            <div className="flex justify-between items-start">
                <div className="overflow-hidden">
                    <h3 className="font-medium text-xl text-blue-400 break-all tracking-tight uppercase">{pbn.domain}</h3>
                    <p className="text-[10px] font-normal text-slate-500 uppercase mt-1">Created by: {pbn.authorEmail}</p>
                </div>
                <button onClick={() => setIsExpanded(!isExpanded)} className="text-slate-400 hover:text-white transition p-3 bg-slate-900/50 rounded-xl border border-slate-700/50 shadow-inner">
                    <i className={`fas ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                </button>
            </div>
            {isExpanded && (
                <div className="mt-6 pt-6 border-t border-slate-700">
                    <div className="space-y-4 max-h-60 overflow-y-auto pr-2 mb-6 scrollbar-hide">
                        {pbn.comments?.sort((a,b) => (b.createdAt?.toDate?.() || b.createdAt).getTime() - (a.createdAt?.toDate?.() || a.createdAt).getTime()).map((c, i) => (
                            <div key={i} className="bg-slate-900/60 p-4 rounded-2xl border border-slate-700/50">
                                <p className="text-slate-300 text-sm italic font-normal">"{c.text}"</p>
                                <div className="text-[10px] font-normal text-slate-500 text-right mt-2 uppercase tracking-tighter">
                                    {c.authorEmail} • {new Date(c.createdAt?.toDate?.() || c.createdAt).toLocaleString('en-US')}
                                </div>
                            </div>
                        ))}
                        {(!pbn.comments || pbn.comments.length === 0) && <p className="text-center text-slate-600 text-sm italic font-normal">No progress logs found.</p>}
                    </div>
                    <form onSubmit={handleAddComment} className="flex gap-3">
                        <input type="text" value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Log progress update..." className="flex-grow p-3 bg-slate-700 border border-slate-600 rounded-xl text-white text-sm outline-none focus:border-blue-500 transition shadow-inner font-normal"/>
                        <button type="submit" disabled={loading} className="bg-blue-600 text-white px-5 rounded-xl hover:bg-blue-700 transition disabled:opacity-50 shadow-lg"><i className="fas fa-paper-plane"></i></button>
                    </form>
                </div>
            )}
        </div>
    );
}

function PbnReportPage({ user, pbns }) {
    const [newDomain, setNewDomain] = useState('');
    const [loading, setLoading] = useState(false);

    const handleAddPbn = async (e) => {
        e.preventDefault();
        if (!newDomain.trim()) return;
        setLoading(true);
        try {
            await addDoc(collection(db, "pbns"), { domain: newDomain, authorEmail: user.email, authorId: user.uid, createdAt: serverTimestamp(), comments: [] });
            setNewDomain('');
        } catch (err) { console.error(err); }
        setLoading(false);
    };
    
    return (
        <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto font-normal">
            <h2 className="text-4xl font-medium text-slate-100 mb-8 tracking-tighter uppercase">PBN Inventory</h2>
            <form onSubmit={handleAddPbn} className="mb-12 p-6 bg-slate-800 rounded-3xl border border-slate-700 flex flex-col sm:flex-row gap-4 items-center shadow-2xl">
                <div className="relative w-full">
                    <i className="fas fa-globe absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"></i>
                    <input type="text" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="Enter PBN domain name..." className="w-full p-4 pl-12 bg-slate-700 border border-slate-600 rounded-2xl text-white font-medium outline-none focus:ring-2 focus:ring-blue-500 transition shadow-inner"/>
                </div>
                <button type="submit" disabled={loading} className="w-full sm:w-auto bg-blue-600 text-white px-10 py-4 rounded-2xl font-medium hover:bg-blue-700 transition flex items-center justify-center gap-3 shadow-xl active:scale-95 whitespace-nowrap"><i className="fas fa-plus"></i> <span>Add Domain</span></button>
            </form>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pbns.map(p =>(<PbnItem key={p.id} pbn={p} user={user} />))}
                {pbns.length === 0 && <div className="col-span-full text-center py-20 text-slate-500 font-medium uppercase tracking-widest">Inventory is empty.</div>}
            </div>
        </div>
    );
}

//================================================================================
// Tasks Page Component
//================================================================================
function NewTaskForm({ user, onTaskAdded, setLoading }) {
    const CATEGORIES = ["TOTO855", "FANTASTIC4D", "BOS5000", "IRON4D", "CANDUAN188", "ETHER777", "ELLOSLOT", "PIKASLOT", "SPACEX168", "LINK5000", "CCTVSLOT", "CUAN855"];
    const [data, setData] = useState({ kategori: CATEGORIES[0], namaTugas: '', notes: '', status: 'New Task' });

    const sendTaskNotification = async (task) => {
        const botToken = "8200057133:AAGiuk4quww5npY29mi5zB2XKwga39FPiOY";
        const chatId = "-1002410368560";
        const msg = `*New Task Assigned*\n\n*Category:* \`${task.kategori}\`\n*Operator:* \`${user.email}\`\n*Time:* \`${new Date().toLocaleString('en-US')}\`\n*Task:* \`${task.namaTugas}\`\n${task.notes ? `*Notes:*\n\`\`\`\n${task.notes}\n\`\`\`\n` : ''}*Status:* \`${task.status}\``;
        try { await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ chat_id: chatId, text: msg, parse_mode: 'Markdown' }) }); } catch (err) { console.error(err); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!data.namaTugas.trim()) return;
        setLoading(true);
        try {
            await addDoc(collection(db, 'tasks'), { ...data, authorEmail: user.email, authorId: user.uid, createdAt: serverTimestamp() });
            await sendTaskNotification(data);
            onTaskAdded();
        } catch (err) { console.error(err); }
        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit} className="p-8 bg-slate-800 rounded-3xl border border-slate-700 space-y-6 mb-12 shadow-2xl font-normal">
            <h3 className="text-2xl font-medium text-slate-100 tracking-tight">Assign New Task</h3>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><label className="text-[10px] font-medium text-slate-500 uppercase tracking-widest mb-2 block">Category</label><select value={data.kategori} onChange={(e) => setData({...data, kategori: e.target.value})} className="w-full p-3 bg-slate-700 border border-slate-600 rounded-xl text-white font-medium outline-none shadow-inner">{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                <div><label className="text-[10px] font-medium text-slate-500 uppercase tracking-widest mb-2 block">Initial Status</label><select value={data.status} onChange={(e) => setData({...data, status: e.target.value})} className="w-full p-3 bg-slate-700 border border-slate-600 rounded-xl text-white font-medium outline-none shadow-inner">{["New Task", "In Progress", "Completed"].map(s => <option key={s} value={s}>{s}</option>)}</select></div>
             </div>
             <div><label className="text-[10px] font-medium text-slate-500 uppercase tracking-widest mb-2 block">Task Title</label><input type="text" value={data.namaTugas} onChange={(e) => setData({...data, namaTugas: e.target.value})} placeholder="What needs to be done?" className="w-full p-4 bg-slate-700 border border-slate-600 rounded-xl text-white font-normal outline-none focus:ring-2 focus:ring-blue-500 transition shadow-inner"/></div>
             <div><label className="text-[10px] font-medium text-slate-500 uppercase tracking-widest mb-2 block">Instructions / Notes</label><textarea value={data.notes} onChange={(e) => setData({...data, notes: e.target.value})} rows="3" placeholder="Provide extra context here..." className="w-full p-4 bg-slate-700 border border-slate-600 rounded-xl text-white font-normal outline-none focus:ring-2 focus:ring-blue-500 transition shadow-inner"></textarea></div>
            <div className="flex justify-end gap-4 pt-4"><button type="button" onClick={onTaskAdded} className="text-slate-400 hover:text-white font-medium uppercase text-xs tracking-widest px-6 py-2">Discard</button><button type="submit" className="bg-blue-600 text-white px-10 py-4 rounded-2xl font-medium hover:bg-blue-700 transition shadow-xl active:scale-95">Save Task</button></div>
        </form>
    );
}

function TasksPage({ user, tasks }) {
    const [showForm, setShowForm] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);

    const updateStatus = async (id, status) => {
        setUpdatingId(id);
        try { await updateDoc(doc(db, 'tasks', id), { status }); } catch (err) { console.error(err); }
        setUpdatingId(null);
    };
    
    return (
        <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto w-full font-normal">
            <div className="flex justify-between items-center mb-10">
                <h2 className="text-4xl font-medium text-slate-100 tracking-tighter uppercase">Task Board</h2>
                <button onClick={() => setShowForm(!showForm)} className="bg-blue-600 text-white h-14 w-14 rounded-2xl flex items-center justify-center font-normal text-2xl hover:bg-blue-700 transition shadow-xl active:scale-95"><i className={`fas ${showForm ? 'fa-times' : 'fa-plus'}`}></i></button>
            </div>
            {showForm && <NewTaskForm user={user} onTaskAdded={() => setShowForm(false)} setLoading={()=>{}}/>}
            <div className="space-y-6">
                {tasks.map(t => {
                    const cfg = { "New Task": "bg-slate-600", "In Progress": "bg-yellow-600", "Completed": "bg-green-600" };
                    return (
                        <div key={t.id} className="bg-slate-800 p-8 rounded-3xl border border-slate-700 shadow-xl transition-all hover:border-blue-500/30">
                            <div className="flex flex-col lg:flex-row justify-between items-start gap-6 font-normal">
                                <div className="grow">
                                    <div className="flex flex-wrap items-center gap-3 mb-3">
                                        <p className="font-medium text-slate-100 text-2xl tracking-tight uppercase">{t.namaTugas}</p>
                                        <span className="text-[10px] bg-blue-600/10 border border-blue-400/20 px-3 py-1 rounded-full text-blue-400 font-medium uppercase tracking-widest">{t.kategori}</span>
                                    </div>
                                    {t.notes && <div className="bg-slate-900/50 p-5 rounded-2xl border border-slate-700/50 mb-4 shadow-inner text-slate-300 text-sm whitespace-pre-wrap leading-relaxed italic font-normal">"{t.notes}"</div>}
                                    <div className="text-[10px] text-slate-500 font-medium uppercase tracking-widest flex items-center gap-2">
                                        <i className="fas fa-history"></i>
                                        <span>BY {t.authorEmail} • {t.createdAt ? new Date(t.createdAt.toDate()).toLocaleString('en-US') : 'Syncing...'}</span>
                                    </div>
                                </div>
                                <div className="flex flex-row lg:flex-col items-center lg:items-end gap-4 shrink-0 w-full lg:w-auto pt-4 lg:pt-0">
                                    <span className={`px-5 py-2 text-[10px] font-medium rounded-full text-white uppercase tracking-[0.2em] shadow-lg whitespace-nowrap ${cfg[t.status] || 'bg-slate-600'}`}>{t.status || 'NEW TASK'}</span>
                                    <div className="flex gap-2">
                                        {(t.status === 'New Task' || !t.status) && (
                                            <button onClick={() => updateStatus(t.id, 'In Progress')} disabled={updatingId === t.id} className="bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 px-4 py-2 rounded-xl text-xs font-medium hover:bg-yellow-500 hover:text-white transition uppercase tracking-widest active:scale-95 disabled:opacity-50">Start Work</button>
                                        )}
                                        {t.status === 'In Progress' && (
                                            <button onClick={() => updateStatus(t.id, 'Completed')} disabled={updatingId === t.id} className="bg-green-500/10 text-green-500 border border-green-500/20 px-4 py-2 rounded-xl text-xs font-medium hover:bg-green-500 hover:text-white transition uppercase tracking-widest active:scale-95 disabled:opacity-50">Mark Finished</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {tasks.length === 0 && <div className="text-center py-20 text-slate-600 font-medium uppercase tracking-widest text-sm">Task board is currently empty.</div>}
            </div>
        </div>
    );
}

//================================================================================
// Main App Component
//================================================================================
export default function App() {
    const [user, setUser] = useState(null);
    const [page, setPage] = useState('dashboard');
    const [pbns, setPbns] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const link = document.createElement('link');
        link.rel = 'stylesheet'; link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css';
        document.head.appendChild(link);
        const unsubAuth = onAuthStateChanged(auth, u => { setUser(u); setLoading(false); });
        return () => { unsubAuth(); };
    }, []);

    useEffect(() => {
        if (!user) return;
        const unsubP = onSnapshot(query(collection(db, 'pbns'), orderBy('createdAt', 'desc')), s => setPbns(s.docs.map(d => ({...d.data(), id: d.id}))));
        const unsubT = onSnapshot(query(collection(db, 'tasks'), orderBy('createdAt', 'desc')), s => setTasks(s.docs.map(d => ({...d.data(), id: d.id}))));
        return () => { unsubP(); unsubT(); };
    }, [user]);

    const handleLogin = async (e, p) => {
        try {
            await signInWithEmailAndPassword(auth, e, p);
            setError('');
        } catch (err) {
            setError('Login Failed. Please verify your credentials.');
        }
    };

    if (loading) return (
        <div className="h-screen bg-slate-900 flex items-center justify-center">
            <div className="flex flex-col items-center gap-6">
                <div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-blue-600"></div>
                <p className="text-slate-400 font-medium tracking-[0.3em] uppercase text-xs animate-pulse">Initializing System</p>
            </div>
        </div>
    );

    if (!user) return <LoginPage onLogin={handleLogin} error={error} setLoading={setLoading} />;

    return (
        <div className="h-screen bg-slate-950 font-sans flex flex-col overflow-hidden text-slate-200 font-normal">
           <Header user={user} onNavigate={setPage} onLogout={()=>signOut(auth).then(()=>setPage('dashboard'))} currentPage={page} />
           <main className="grow overflow-y-auto bg-gradient-to-b from-slate-900 to-slate-950">
               {page === 'dashboard' && <DashboardPage user={user} onNavigate={setPage} />}
               {page === 'new_report' && <NewReportForm user={user} onNavigate={setPage} setLoading={setLoading}/>}
               {page === 'pbn_report' && <PbnReportPage user={user} pbns={pbns} />}
               {page === 'tasks' && <TasksPage user={user} tasks={tasks} />}
               {page === 'reports' && <ReportsPage onNavigate={setPage} />}
           </main>
           <footer className="text-center p-6 text-[9px] text-slate-700 border-t border-slate-900 shrink-0 font-medium uppercase tracking-[0.5em] bg-slate-950">
                © {new Date().getFullYear()} Report Tools • Engine by Venom777 • Version 1.7
           </footer>
        </div>
    );
}